// AuthProvider.js
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { APP_REQUEST, API_REQUEST } from '../helpers/request';

const AuthContext = createContext();

// Global logout handler registry
let globalLogoutHandler = null;

export const setGlobalLogoutHandler = (fn) => {
  globalLogoutHandler = fn;
};

export const triggerGlobalLogout = (
  message = 'Your session has expired. Please log in again.'
) => {
  if (typeof globalLogoutHandler === 'function') {
    globalLogoutHandler(message);
  } else {
    window.location.href = '/intro';
  }
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const authChecked = useRef(false);

  // Centralized logout function
  const logout = useCallback(async () => {
    try {
      // Call logout API if we think we're logged in
      if (loggedIn) {
        await API_REQUEST.get('auth/logout');
      }
    } catch (_error) {
      // Logout API failure is non-critical
    } finally {
      // Always clear local state
      setUser(null);
      setLoggedIn(false);

      // Redirect to login page
      APP_REQUEST.redirect('/intro');
    }
  }, [loggedIn]);

  // Register the logout handler globally when component mounts
  useEffect(() => {
    setGlobalLogoutHandler(logout);

    // Cleanup on unmount
    return () => {
      setGlobalLogoutHandler(null);
    };
  }, [logout]);

  const register = async (
    email,
    firstName = '',
    lastName = '',
    mobile = '',
    company = '',
    acceptNewsletter = false,
    systemInfo = null
  ) => {
    try {
      const response = await API_REQUEST.post('auth/register', {
        email,
        firstName,
        lastName,
        mobile,
        company,
        acceptNewsletter,
        os: systemInfo?.os,
        browser: systemInfo?.browser,
      });

      if (response.status === 201) {
        const { user: userData, warning } = response.data;
        setUser(userData);
        return {
          success: true,
          message: response.data.message,
          warning,
        };
      }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          'Registration failed. Please try again.',
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await API_REQUEST.post('auth/login', {
        email,
        password,
      });

      if (response.status === 200) {
        const { user: userData, warning } = response.data;
        setUser(userData);
        setLoggedIn(true);
        APP_REQUEST.redirect('dashboard');
        return {
          success: true,
          message: response.data.message,
          warning,
        };
      }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || 'Login failed. Please try again.',
      };
    }
  };

  const checkAuth = async () => {
    try {
      const response = await API_REQUEST.get('auth/check');
      const { isAuthenticated, user: userData } = response.data;
      setLoggedIn(isAuthenticated);
      setUser(userData || null);
      return isAuthenticated;
    } catch (_error) {
      setLoggedIn(false);
      setUser(null);
      return false;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (authChecked.current) return;
      authChecked.current = true;

      await checkAuth();
      setLoading(false);
    };

    initAuth();
  }, []);

  const redirectIfLoggedIn = (redirectPath = 'dashboard') => {
    if (!loading && loggedIn) {
      APP_REQUEST.redirect(redirectPath);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        register,
        login,
        logout,
        loading,
        loggedIn,
        redirectIfLoggedIn,
        setLoggedIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthProvider;
